import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  BarChart, Bar, LineChart, Line, ComposedChart,
  ScatterChart, Scatter, ZAxis, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, LabelList, Cell,
} from "recharts";

// ⚠️ Adjust this path to wherever your map component lives.
import MapDDRWithKpiHeatmap from "./MapDDRWithKpiHeatmap";
import { DDR_STATE, STATE_CODE } from "./Ddrnames";
import KoCampaView from "./Kocampaview";
import { parseKoCampaWorkbook, hasKoCampaSheets } from "./Kocampaparse";
import { findInsurgentSheet } from "./insurgentparse";
import WdTableView from "./Wdtableview";
import { findWdSheet } from "./Wdparse";
import BrandView from "./Brandview";
import { findBrandSheet } from "./Brandparse";

/* ══════════════════════════════════════════════════════════════════════
   THEME (self-contained on purpose — avoids a circular import with
   Dashboard.jsx, which imports this file)
   ══════════════════════════════════════════════════════════════════════ */
const T = {
  bg: "#FAFAFA", surface: "#FFFFFF", bgAlt: "#FFF8F8",
  border: "#FADDDD", borderMed: "#F5BABA",
  accent: "#E8001A", accentDark: "#B50015", accentDeep: "#800010",
  accentSoft: "#FFF0F0",
  text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060",
  pos: "#1A7A3A", neg: "#C42020", warn: "#B06000", neutral: "#8A7070",
  shadow: "0 2px 8px rgba(232,0,26,0.08)",
  shadowMd: "0 4px 16px rgba(232,0,26,0.12)",
};

/* ── Segment definitions (match the stacked bars in the source deck) ── */
export const DDR_SEGMENTS = [
  { key: "affKo",       label: "Aff KO@10",                    color: "#E8001A" },
  { key: "affKoCampa",  label: "Aff KO@10 & Campa@10",         color: "#FFD400" },
  { key: "exKoCampa",   label: "Exclusive KO SSPET & Campa@10",color: "#EDEDED" },
  { key: "exCampa",     label: "Exclusive Campa@10",           color: "#9B8BC4" },
  { key: "exKo",        label: "Exclusive KO SSPET",           color: "#B50015" },
];

export const DDR_LINES = [
  { key: "pci",      label: "PCI@10",   color: "#1F3C88" },
  { key: "affCci",   label: "Aff CCI@10", color: "#E8001A" },
  { key: "campa10",  label: "CAMPA@10", color: "#C08BD4" },
  { key: "cciSspet", label: "CCI SSPET", color: "#C42020" },
];

export const DDR_COMPETITORS = [
  { key: "lahori", label: "Lahori",  color: "#1F3C88" },
  { key: "rimzim", label: "Rim-Zim", color: "#E8701A" },
];

/* The legend/axis text must follow the ACTIVE sheet's band (@10 / @20 /
   @10+20). DDR_SEGMENTS carries "@10" as the canonical label, so we detect
   the band from the sheet's real headers and substitute it in. */
export function bandOf(sheetName, colMap) {
  const probes = [colMap?.affCci, colMap?.campa10, colMap?.affKo, colMap?.pci, sheetName];
  for (const probe of probes) {
    const m = String(probe || "").match(/@\s*([\d]+(?:\s*\+\s*[\d]+)*)/);
    if (m) return m[1].replace(/\s+/g, "");
  }
  const fromName = String(sheetName || "").match(/([\d]+(?:\s*\+\s*[\d]+)*)\s*$/);
  return fromName ? fromName[1].replace(/\s+/g, "") : "10";
}

export const relabel = (label, band) => String(label).replace(/@\s*10\b(?!\s*\+)/g, `@${band}`);

export const SHEET_COLORS = ["#E8001A", "#1A7A9A", "#B06000", "#5A8A2A", "#7A3A8A"];

const MONTH_ORDER = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

/* DDR labels arrive as "UttarPradesh-11+12" / "MP-8" etc. The map component
   matches on GeoJSON `properties.Name`, so strip the trailing "-<digits>[+<digits>]"
   route code and split the CamelCase run back into words. */
export function ddrToStateName(ddr) {
  let s = String(ddr || "").trim();
  s = s.replace(/[\s_-]*\d+(\s*\+\s*\d+)*\s*$/, "");   // drop "-11+12"
  s = s.replace(/[-_]+$/, "").trim();
  s = s.replace(/([a-z])([A-Z])/g, "$1 $2");                // UttarPradesh -> Uttar Pradesh
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

/* ══════════════════════════════════════════════════════════════════════
   COLUMN MATCHING — the headers in the file are messy
   ("Aff KO@10 No Campa @ 10"), so match on patterns instead of literals.
   Order matters: the "& Campa" variants must be tested before the plain ones.
   ══════════════════════════════════════════════════════════════════════ */
/* The metric columns carry the store-band suffix, which CHANGES PER SHEET:
     "Stores @ 10"    -> Aff CCI@10,    Aff KO@10 No Campa @ 10
     "Stores @ 20"    -> Aff CCI@20,    Aff KO@20 No Campa @ 20
     "Stores @ 10+20" -> Aff CCI@10+20, ...
   So the patterns must NOT hard-code "10" — they allow any digit/plus run
   after the "@". Order matters: "& Campa" variants are tested before the
   plain ones so the plain pattern doesn't swallow them. */
const BAND = "\\s*@?\\s*[\\d+\\s]*";

const COL_PATTERNS = [
  ["ddr",        /^ddr$/i],
  ["year",       /^year$/i],
  ["month",      /^month$/i],
  ["affKoCampa", new RegExp(`aff\\s*ko${BAND}.*(&|and).*campa`, "i")],
  ["affKo",      new RegExp(`aff\\s*ko${BAND}`, "i")],
  ["exKoCampa",  /exclusive\s*ko\s*sspet.*(&|and).*campa/i],
  ["exCampa",    /exclusive\s*campa/i],
  ["exKo",       /exclusive\s*ko\s*sspet/i],
  ["affCci",     /aff\s*cci/i],
  ["pci",        /\bpci\b/i],
  ["campa10",    /^\s*campa\s*@/i],
  ["cciSspet",   /cci\s*sspet/i],
  ["lahori",     /lahori/i],
  ["rimzim",     /rim\s*[-\s]?\s*zim/i],
];

/* Rows whose DDR cell is a pivot artefact rather than a real territory */
const JUNK_DDR = /^\s*(\(?all\)?|total|grand\s*total|year|month|ddr|sum|n\/?a|-+)\s*$/i;

/* Sheets that are pivot scaffolding, not data */
const JUNK_SHEET = /pivot|^sheet\d*$|chart|raw\s*data\s*$/i;

function buildColumnMap(headers) {
  const map = {};
  const taken = new Set();
  COL_PATTERNS.forEach(([canon, re]) => {
    if (map[canon]) return;
    const hit = headers.find(h => h && !taken.has(h) && re.test(String(h).trim()));
    if (hit) { map[canon] = hit; taken.add(hit); }
  });
  return map;
}

const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(String(v).replace(/,/g, "").replace(/%/g, ""));
  return isNaN(n) ? 0 : n;
};

/* ══════════════════════════════════════════════════════════════════════
   WORKBOOK DETECTION + PARSING
   ══════════════════════════════════════════════════════════════════════ */
export function isDDRWorkbook(workbook) {
  return workbook.SheetNames.some(name => {
    const sheet = workbook.Sheets[name];
    if (!sheet || !sheet["!ref"]) return false;
    const flat = JSON.stringify(sheet).toLowerCase();
    return /"ddr"/.test(flat) && /rim.?zim|campa\s*@\s*10/.test(flat);
  });
}

export function parseDDRWorkbook(workbook, XLSX) {
  const allRows = [];
  const sheets = [];
  const colMapBySheet = {};
  let colMapSeen = null;

  for (const sheetName of workbook.SheetNames) {
    if (JUNK_SHEET.test(sheetName)) continue;          // drop "Pivot-1" etc.
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;
    const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });
    if (!raw.length) continue;

    // Header row is NOT row 1 — these files carry title/blank rows above it.
    // Scan the top 15 rows for the one containing a cell equal to "DDR".
    let headerIdx = -1;
    for (let i = 0; i < Math.min(15, raw.length); i++) {
      const row = raw[i] || [];
      if (row.some(c => typeof c === "string" && /^\s*ddr\s*$/i.test(c))) { headerIdx = i; break; }
    }
    if (headerIdx < 0) continue;

    // Leading blank spacer columns are normal here — indexOf on the raw header
    // array keeps the positional mapping correct regardless.
    const headers = (raw[headerIdx] || []).map(h => (h == null ? "" : String(h).trim()));
    const colMap = buildColumnMap(headers);
    if (!colMap.ddr) continue;
    if (!DDR_SEGMENTS.some(seg => colMap[seg.key])) continue;   // no metrics = not a data sheet
    colMapSeen = colMapSeen || colMap;

    const idxOf = {};
    Object.entries(colMap).forEach(([canon, header]) => { idxOf[canon] = headers.indexOf(header); });

    let count = 0;
    for (let i = headerIdx + 1; i < raw.length; i++) {
      const r = raw[i];
      if (!r || !r.some(c => c !== null && c !== undefined && c !== "")) continue;
      const ddr = r[idxOf.ddr];
      if (typeof ddr !== "string" || !ddr.trim()) continue;
      if (JUNK_DDR.test(ddr)) continue;                 // "(All)", "Total", "Year"

      const rec = { ddr: ddr.trim(), sheet: sheetName };
      // Authoritative DDR -> State lookup, with a derived fallback for any
      // DDR missing from the mapping table.
      rec.state = DDR_STATE[rec.ddr]
        || DDR_STATE[rec.ddr.trim()]
        || ddrToStateName(rec.ddr);
      rec.stateName = rec.state;

      Object.keys(idxOf).forEach(canon => {
        if (canon === "ddr") return;
        const v = r[idxOf[canon]];
        rec[canon] = (canon === "year" || canon === "month") ? v : num(v);
      });

      const monthRaw = String(rec.month ?? "").trim();
      const mon3 = monthRaw.slice(0, 3).toLowerCase();
      const yearRaw = String(rec.year ?? "").trim();
      const yy = yearRaw.length >= 4 ? yearRaw.slice(-2) : yearRaw;
      rec.monthLabel = mon3 ? `${mon3[0].toUpperCase()}${mon3.slice(1)}'${yy}` : yearRaw;
      rec.sortKey = (Number(yearRaw.length >= 4 ? yearRaw : `20${yy}`) || 0) * 12
                    + Math.max(0, MONTH_ORDER.indexOf(mon3));

      rec.koCampaStores = DDR_SEGMENTS.reduce((s, seg) => s + (rec[seg.key] || 0), 0);
      allRows.push(rec);
      count++;
    }
    if (count) { sheets.push(sheetName); colMapBySheet[sheetName] = colMap; }
  }

  // KO_VOLUME / CAMPA_VOLUME live in the same workbook but have a totally
  // different shape, so they get their own parser and their own tab.
  const koCampa = hasKoCampaSheets(workbook) ? parseKoCampaWorkbook(workbook, XLSX) : null;

  // Insurgent-brands bubble sheet (Bottler / DDR / SPK+ED vol / share / WD)
  let insurgent = null;
  try { insurgent = findInsurgentSheet(workbook, XLSX); }
  catch (e) { console.warn("insurgent sheet parse failed", e); }

  // WD / VPO / IPS coverage sheet (merged header rows, own tab + bottom card).
  // findWdSheet returns { wd, debug } — debug lists every sheet probed and why
  // it was rejected, so a miss shows up in the UI instead of silently hiding.
  let wd = null, wdDebug = [];
  try {
    const res = findWdSheet(workbook, XLSX);
    wd = res?.wd || null;
    wdDebug = res?.debug || [];
    if (!wd) console.warn("[WD] no sheet parsed:", wdDebug);
  } catch (e) { console.warn("WD sheet parse failed", e); }

  // 10_brand pivot — brand x pack x state WD% / VPO
  let brand = null, brandDebug = [];
  try {
    const res = findBrandSheet(workbook, XLSX);
    brand = res?.brand || null;
    brandDebug = res?.debug || [];
    if (!brand) console.warn("[10_brand] no sheet parsed:", brandDebug);
  } catch (e) { console.warn("brand sheet parse failed", e); }

  if (!allRows.length) {
    // Workbook may contain ONLY the competitive / coverage sheets
    return (koCampa || wd || brand) ? { isDDR: true, isHCCB: false, rows: [], months: [], ddrs: [], years: [],
      sheets: [], defaultSheet: "", latestMonth: "", koCampa, insurgent,
      wd, wdDebug, brand, brandDebug,
      cboData: [{ l3: "KO/Campa", l2: "-", vol: 0, volMix: 0, ytdGr: 0, qtdGr: 0, mtdGr: 0, cagr2y: 0, ytdCyc: 0 }],
      catData: [], momData: {}, momCat: {}, totals: {} } : null;
  }

  const months = [...new Map(allRows.map(r => [r.monthLabel, r.sortKey])).entries()]
    .sort((a, b) => a[1] - b[1]).map(([label]) => label);
  const ddrs = [...new Set(allRows.map(r => r.ddr))].sort();
  const years = [...new Set(allRows.map(r => String(r.year ?? "")).filter(Boolean))].sort();

  // Prefer a "@ 10" sheet as the landing tab, else the first one
  const defaultSheet = sheets.find(n => /@\s*10\s*$/i.test(n)) || sheets[0];

  return {
    isDDR: true,
    isHCCB: false,
    rows: allRows,
    months,
    ddrs,
    years,
    sheets,
    defaultSheet,
    latestMonth: months[months.length - 1] || "",
    columnMap: colMapSeen,
    columnMapBySheet: colMapBySheet,
    koCampa,
    insurgent,
    wd,
    wdDebug,
    brand,
    brandDebug,
    cboData: ddrs.map(d => ({ l3: d, l2: "DDR", vol: 0, volMix: 0, ytdGr: 0, qtdGr: 0, mtdGr: 0, cagr2y: 0, ytdCyc: 0 })),
    catData: [], momData: {}, momCat: {}, totals: {},
  };
}

/* ══════════════════════════════════════════════════════════════════════
   SHARED UI
   ══════════════════════════════════════════════════════════════════════ */
const fmtK = (v) => {
  if (v == null || isNaN(v)) return "—";
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)} K`;
  return String(Math.round(v));
};
const fmtPct = (v, d = 0) => (v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(d)}%`);
const growthColor = (v) => (v == null ? T.neutral : v > 0.0005 ? T.pos : v < -0.0005 ? T.neg : T.neutral);

const Tip = ({ active, payload, label, pct }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(255,255,255,0.97)", border: `1px solid ${T.borderMed}`,
      borderRadius: 8, padding: "10px 14px", fontSize: 11, color: T.text, boxShadow: T.shadowMd }}>
      <div style={{ color: T.textDim, marginBottom: 6, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color || p.fill }} />
            <span style={{ color: T.textMid }}>{p.name}</span>
          </span>
          <span style={{ color: T.text, fontWeight: 600 }}>
            {pct ? `${Number(p.value).toFixed(1)}%` : fmtK(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};


/* ══════════════════════════════════════════════════════════════════════
   MULTI-SELECT — the DDR list runs to 150+ entries, so chips are unusable.
   ══════════════════════════════════════════════════════════════════════ */
function MultiSelect({ label, options, selected, onChange, width = 240 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter(o => o.toLowerCase().includes(needle)) : options;
  }, [options, q]);

  const toggle = (o) => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o]);
  const summary = selected.length === 0 ? "All" : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 8, width,
        background: T.surface, border: `1px solid ${selected.length ? T.accent : T.border}`,
        color: selected.length ? T.accent : T.textMid, padding: "5px 10px",
        borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer",
      }}>
        <span style={{ color: T.textDim, fontSize: 9.5, letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
        <span style={{ fontSize: 9, color: T.textDim }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 300,
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", width: Math.max(width, 280),
        }}>
          <div style={{ padding: 8, borderBottom: `1px solid ${T.border}` }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`}
              style={{ width: "100%", padding: "6px 8px", border: `1px solid ${T.border}`,
                borderRadius: 6, fontSize: 11, outline: "none", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button onClick={() => onChange(shown)} style={miniBtn}>Select shown ({shown.length})</button>
              <button onClick={() => onChange([])} style={miniBtn}>Clear</button>
            </div>
          </div>
          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {shown.length === 0 && <div style={{ padding: 14, fontSize: 11, color: T.textDim, textAlign: "center" }}>No matches</div>}
            {shown.map(o => {
              const on = selected.includes(o);
              return (
                <div key={o} onClick={() => toggle(o)} style={{
                  padding: "6px 10px", display: "flex", alignItems: "center", gap: 8,
                  cursor: "pointer", fontSize: 11.5, background: on ? T.accentSoft : "transparent",
                  color: on ? T.accent : T.text, fontWeight: on ? 600 : 400,
                }}>
                  <span style={{
                    width: 13, height: 13, borderRadius: 3, flexShrink: 0,
                    border: `1px solid ${on ? T.accent : T.border}`,
                    background: on ? T.accent : "transparent", color: "#fff",
                    fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{on ? "✓" : ""}</span>
                  {o}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
const miniBtn = {
  flex: 1, padding: "3px 6px", fontSize: 10, borderRadius: 6,
  border: `1px solid ${T.border}`, background: T.bgAlt, color: T.textMid,
  cursor: "pointer", fontWeight: 600,
};

function Card({ title, subtitle, badge, children, style = {} }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
      padding: 16, display: "flex", flexDirection: "column", boxShadow: T.shadow, ...style }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{title}</div>
          {subtitle && <div style={{ color: T.textDim, fontSize: 10.5, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {badge && <div style={{ background: T.accentSoft, border: `1px solid ${T.borderMed}`, color: T.accent,
          fontSize: 9, padding: "3px 8px", borderRadius: 4, fontWeight: 700 }}>{badge}</div>}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

function KPI({ label, value, delta, subtitle, color, icon }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
      padding: 16, boxShadow: T.shadow, borderTop: `3px solid ${color || T.accent}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ color: T.textDim, fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
          <div style={{ color: T.text, fontSize: 22, fontWeight: 700, marginTop: 6 }}>{value}</div>
          {subtitle && <div style={{ color: T.textDim, fontSize: 10, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {icon && <span style={{ fontSize: 24, opacity: 0.25 }}>{icon}</span>}
      </div>
      {delta !== undefined && delta !== null && (
        <div style={{ marginTop: 10 }}>
          <span style={{ color: growthColor(delta), fontSize: 11, fontWeight: 700, padding: "2px 8px",
            borderRadius: 4, background: delta >= 0 ? "rgba(26,122,58,0.12)" : "rgba(196,32,32,0.1)" }}>
            {delta >= 0 ? "▲" : "▼"} {fmtPct(Math.abs(delta), 1)}
          </span>
          <span style={{ color: T.textDim, fontSize: 10, marginLeft: 6 }}>vs prior month</span>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════════════════════════════ */
export default function DDRDashboard({ data, session, onAskAI, onReset }) {
  const { rows, months, ddrs, years, latestMonth, sheets = [], defaultSheet } = data;

  const [view, setView] = useState(sheets.length ? "outlets" : "competitive");
  const [sheetSel, setSheetSel] = useState(defaultSheet || sheets[0] || "");
  const [monthSel, setMonthSel] = useState(latestMonth);
  const [ddrSel, setDdrSel]     = useState([]);          // [] = all
  const [yearSel, setYearSel]   = useState("__all__");
  const [showMap, setShowMap]   = useState(true);
  const [topN, setTopN]         = useState(15);   // 0 = all
  const [bottlerSel, setBottlerSel] = useState("__all__");

  /* Labels track the active sheet's band, so switching tabs relabels
     every legend, axis, table header and KPI card. */
  const band = useMemo(
    () => bandOf(sheetSel, (data.columnMapBySheet || {})[sheetSel] || data.columnMap),
    [data, sheetSel]
  );
  const segments = useMemo(
    () => DDR_SEGMENTS.map(sg => ({ ...sg, label: relabel(sg.label, band) })),
    [band]
  );
  const lineSeries = useMemo(
    () => DDR_LINES.map(l => ({ ...l, label: relabel(l.label, band) })),
    [band]
  );

  /* The WD table follows the active sheet tab: "Stores @ 10" shows only the
     @10 packs, "Stores @ 20" only the @20 packs, and "@10+20" shows both
     (null = no lock). */
  const wdBandLock = useMemo(
    () => (!band || band.includes("+") ? null : band),
    [band]
  );

  /* Scope = DDR + year filters (month handled per-chart) */
  const scoped = useMemo(() => rows.filter(r =>
    (!sheetSel || r.sheet === sheetSel) &&
    (ddrSel.length === 0 || ddrSel.includes(r.ddr)) &&
    (yearSel === "__all__" || String(r.year) === yearSel)
  ), [rows, sheetSel, ddrSel, yearSel]);

  /* ── Monthly roll-up across all DDRs in scope ── */
  const byMonth = useMemo(() => {
    const acc = {};
    scoped.forEach(r => {
      if (!acc[r.monthLabel]) acc[r.monthLabel] = { month: r.monthLabel, sortKey: r.sortKey, total: 0 };
      const m = acc[r.monthLabel];
      [...DDR_SEGMENTS, ...DDR_LINES, ...DDR_COMPETITORS].forEach(({ key }) => {
        m[key] = (m[key] || 0) + (r[key] || 0);
      });
      m.total += r.koCampaStores || 0;
    });
    return Object.values(acc).sort((a, b) => a.sortKey - b.sortKey);
  }, [scoped]);

  /* ── Same series, normalised to 100% (the "Outlet%" chart) ── */
  const byMonthPct = useMemo(() => byMonth.map(m => {
    const out = { month: m.month };
    const total = DDR_SEGMENTS.reduce((s, seg) => s + (m[seg.key] || 0), 0) || 1;
    DDR_SEGMENTS.forEach(seg => { out[seg.key] = ((m[seg.key] || 0) / total) * 100; });
    return out;
  }), [byMonth]);

  /* ── Per-DDR breakdown for the selected month ── */
  const byDdr = useMemo(() => {
    const acc = {};
    scoped.filter(r => r.monthLabel === monthSel).forEach(r => {
      if (!acc[r.ddr]) acc[r.ddr] = { ddr: r.ddr, total: 0 };
      const d = acc[r.ddr];
      [...DDR_SEGMENTS, ...DDR_LINES, ...DDR_COMPETITORS].forEach(({ key }) => {
        d[key] = (d[key] || 0) + (r[key] || 0);
      });
      d.total += r.koCampaStores || 0;
    });
    return Object.values(acc).sort((a, b) => b.total - a.total);
  }, [scoped, monthSel]);

  const byDdrTop = useMemo(
    () => (topN > 0 ? byDdr.slice(0, topN) : byDdr),
    [byDdr, topN]
  );

  /* ── State roll-up for the selected month ── */
  const byState = useMemo(() => {
    const acc = {};
    scoped.filter(r => r.monthLabel === monthSel).forEach(r => {
      const st = r.state || "Unmapped";
      if (!acc[st]) acc[st] = { state: st, code: STATE_CODE[st] || st, total: 0, ddrs: 0 };
      const a = acc[st];
      [...DDR_SEGMENTS, ...DDR_LINES, ...DDR_COMPETITORS].forEach(({ key }) => {
        a[key] = (a[key] || 0) + (r[key] || 0);
      });
      a.total += r.koCampaStores || 0;
      a.ddrs += 1;
    });
    // Sorted by size, not alphabetically — the deck's state view ranks them
    return Object.values(acc).sort((a, b) => b.total - a.total);
  }, [scoped, monthSel]);

  /* ── Cross-sheet comparison: same DDR/year scope, every store band ── */
  const crossSheet = useMemo(() => {
    const inScope = rows.filter(r =>
      (ddrSel.length === 0 || ddrSel.includes(r.ddr)) &&
      (yearSel === "__all__" || String(r.year) === yearSel)
    );
    const acc = {};
    inScope.forEach(r => {
      if (!acc[r.monthLabel]) acc[r.monthLabel] = { month: r.monthLabel, sortKey: r.sortKey };
      acc[r.monthLabel][r.sheet] = (acc[r.monthLabel][r.sheet] || 0) + (r.koCampaStores || 0);
    });
    return Object.values(acc).sort((a, b) => a.sortKey - b.sortKey);
  }, [rows, ddrSel, yearSel]);

  /* ── Biggest month-over-month movers in the active sheet ── */
  const movers = useMemo(() => {
    if (byMonth.length < 2) return [];
    const idx = months.indexOf(monthSel);
    const prevMonth = idx > 0 ? months[idx - 1] : null;
    if (!prevMonth) return [];
    const cur = {}, prev = {};
    // Movers measured on Aff KO@10 distribution, not total stores
    scoped.forEach(r => {
      if (r.monthLabel === monthSel)   cur[r.ddr]  = (cur[r.ddr]  || 0) + (r.affKo || 0);
      if (r.monthLabel === prevMonth)  prev[r.ddr] = (prev[r.ddr] || 0) + (r.affKo || 0);
    });
    return Object.keys(cur)
      .map(d => ({ ddr: d, delta: (cur[d] || 0) - (prev[d] || 0) }))
      .filter(x => x.delta !== 0)
      .sort((a, b) => b.delta - a.delta);
  }, [scoped, byMonth, months, monthSel]);

  const topMovers = useMemo(() => {
    if (movers.length <= 12) return movers;
    return [...movers.slice(0, 6), ...movers.slice(-6)];
  }, [movers]);

  /* Insurgent bubble points, optionally narrowed to one bottler */
  const insurgentPoints = useMemo(() => {
    const ins = data.insurgent;
    if (!ins) return [];
    return ins.rows
      .filter(r => bottlerSel === "__all__" || r.bottler === bottlerSel)
      .map(r => ({ ...r, x: r.wd, y: r.share, z: r.vol || 0.01 }));
  }, [data.insurgent, bottlerSel]);

  /* ── KPIs: latest month vs prior ── */
const kpis = useMemo(() => {
    const cur  = byMonth[byMonth.length - 1] || {};
    const prev = byMonth[byMonth.length - 2] || {};
    const mom  = (k) => (prev[k] ? (cur[k] || 0) / prev[k] - 1 : null);
    const totalSeg = DDR_SEGMENTS.reduce((s, seg) => s + (cur[seg.key] || 0), 0) || 1;
 return {
      affCci:       cur.affCci || 0,
      affCciMom:    mom("affCci"),
      pci:          cur.pci || 0,
      pciMom:       mom("pci"),
      lahori:       cur.lahori || 0,
      lahoriMom:    mom("lahori"),
      koCampa:      cur.total || 0,
      campa10Mom:   mom("campa10"),
      koCampaMom:   mom("total"),
      exKo:         cur.exKo || 0,
      exKoShare:    (cur.exKo || 0) / totalSeg,
      campaAny:     (cur.affKoCampa || 0) + (cur.exKoCampa || 0) + (cur.exCampa || 0),
      campaShare:   ((cur.affKoCampa || 0) + (cur.exKoCampa || 0) + (cur.exCampa || 0)) / totalSeg,
      cciSspet:     cur.cciSspet || 0,
      cciMom:       mom("cciSspet"),
      rimzim:       cur.rimzim || 0,
      rimzimMom:    mom("rimzim"),
    };
  }, [byMonth]);


  /* ── Map payload: MapDDRWithKpiHeatmap keys on `state`, so DDR → state.
        Non-numeric keys become its dropdown filters. ── */
  /* Map payload. `segments` drives the hover card's five-way split;
     __affKo / __campa drive the ratio heat map. The raw DDR goes in `state`
     for legacy-name resolution, the mapped State for the state-level view. */
  const mapData = useMemo(() => {
    const src = scoped.filter(r => r.monthLabel === monthSel);
    return src.map(r => ({
      state: r.ddr,
      State: r.state,
      DDR: r.ddr,
      Month: r.monthLabel,
      segments: {
        [relabel("Aff KO@10", band)]:                    r.affKo || 0,
        [relabel("Aff KO@10 & Campa@10", band)]:         r.affKoCampa || 0,
        [relabel("Exclusive KO SSPET & Campa@10", band)]: r.exKoCampa || 0,
        [relabel("Exclusive Campa@10", band)]:           r.exCampa || 0,
        "Exclusive KO SSPET":                            r.exKo || 0,
      },
      // Heat-map ratio inputs — KO = segments 1+2, Campa = segments 2+3+4
      __koBase:    (r.affKo || 0) + (r.affKoCampa || 0),
      __campaBase: (r.affKoCampa || 0) + (r.exKoCampa || 0) + (r.exCampa || 0),
      "KO + Campa Stores": r.koCampaStores,
      "Exclusive KO SSPET": r.exKo,
      [relabel("Aff KO@10", band)]: r.affKo,
      [relabel("CAMPA@10", band)]: r.campa10,
      "CCI SSPET": r.cciSspet,
      "Rim-Zim": r.rimzim,
      "Lahori": r.lahori,
    }));
  }, [scoped, monthSel, band]);

  const stackedBars = () => segments.map((seg, i) => (
    <Bar key={seg.key} dataKey={seg.key} name={seg.label} stackId="a"
      fill={seg.color} stroke={seg.color === "#EDEDED" ? "#D0D0D0" : "none"}
      radius={i === segments.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
  ));

  /* Recharts colours legend text by the series fill — which makes the
     near-white "Exclusive KO SSPET & Campa" entry unreadable. Force it. */
  const legendProps = {
    wrapperStyle: { fontSize: 10 },
    formatter: (value) => <span style={{ color: T.textMid }}>{value}</span>,
  };

  return (
    <div style={{ minHeight: "100%", background: T.bgAlt, color: T.text, paddingBottom: 40 }}>
      {/* ── Header ── */}
      <div style={{ background: T.accent, padding: "14px 24px", display: "flex",
        alignItems: "center", justifyContent: "space-between", boxShadow: T.shadowMd }}>
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: -0.5 }}>
            India Affordability Volume and Distribution Coverage Dashboard
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 10.5, marginTop: 2 }}>
            {ddrs.length} DDRs · {months.length} periods · latest {latestMonth}
            {sheetSel ? ` · ${sheetSel} (@${band})` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {onAskAI && <button onClick={onAskAI} style={{ background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)", color: "#fff", padding: "7px 14px",
            borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>🤖 Ask AI</button>}
          {onReset && <button onClick={onReset} style={{ background: "transparent",
            border: "1px solid rgba(255,255,255,0.4)", color: "#fff", padding: "7px 12px",
            borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>↻ NEW FILE</button>}
        </div>
      </div>
      <div style={{ height: 3, background: `linear-gradient(90deg,${T.accentDeep},${T.accent},${T.accentDeep})` }} />

      {/* ── Top-level view tabs ── */}
      {(data.koCampa || data.wd || data.brand || data.wdDebug?.length > 0) && (
        <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`,
          padding: "8px 20px 0", display: "flex", gap: 6 }}>
          {[["outlets", "🏪 Outlet Coverage", sheets.length > 0],
            ["competitive", "⚔️ KO vs Campa Volume", !!data.koCampa],
            ["wd", "📐 WD · VPO · IPS", !!data.wd || data.wdDebug?.length > 0],
            ["brand", "🥤 Brand @10", !!data.brand || data.brandDebug?.length > 0]]
            .filter(([, , show]) => show).map(([id, lab]) => {
            const on = view === id;
            return (
              <button key={id} onClick={() => setView(id)} style={{
                padding: "8px 18px", border: `1px solid ${on ? T.accent : T.border}`,
                borderBottom: on ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
                borderRadius: "8px 8px 0 0", cursor: "pointer",
                background: on ? T.accentSoft : "transparent",
                color: on ? T.accent : T.textDim,
                fontSize: 12.5, fontWeight: on ? 700 : 600,
              }}>{lab}</button>
            );
          })}
        </div>
      )}

      {view === "competitive" && data.koCampa ? (
        <KoCampaView data={data.koCampa} brand={data.brand} onAskAI={onAskAI} />
      ) : view === "wd" ? (
        <WdTableView data={data.wd} debug={data.wdDebug} onAskAI={onAskAI} />
      ) : view === "brand" ? (
        <BrandView data={data.brand} debug={data.brandDebug} onAskAI={onAskAI} />
      ) : (<>

      {/* ── Sheet tabs ── */}
      {sheets.length > 1 && (
        <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`,
          padding: "8px 20px 0", display: "flex", gap: 4, flexWrap: "wrap" }}>
          {sheets.map(sh => {
            const on = sheetSel === sh;
            return (
              <button key={sh} onClick={() => setSheetSel(sh)} style={{
                padding: "7px 16px", border: `1px solid ${on ? T.accent : T.border}`,
                borderBottom: on ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
                borderRadius: "8px 8px 0 0", cursor: "pointer",
                background: on ? T.accentSoft : "transparent",
                color: on ? T.accent : T.textDim,
                fontSize: 12, fontWeight: on ? 700 : 600,
              }}>{sh}</button>
            );
          })}
        </div>
      )}

      {/* ── Filter bar ── */}
      <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: "10px 20px",
        position: "sticky", top: 0, zIndex: 60, boxShadow: "0 2px 8px rgba(232,0,26,0.06)",
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={lbl}>Month</span>
          <select value={monthSel} onChange={e => setMonthSel(e.target.value)} style={sel}>
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {years.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={lbl}>Year</span>
            <select value={yearSel} onChange={e => setYearSel(e.target.value)} style={sel}>
              <option value="__all__">All</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}

        {/* 150+ territories — searchable dropdown, not chips */}
        <MultiSelect label="DDR" options={ddrs} selected={ddrSel} onChange={setDdrSel} width={260} />

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={lbl}>Show</span>
          <select value={topN} onChange={e => setTopN(Number(e.target.value))} style={sel}>
            {[10, 15, 20, 30, 50].map(n => <option key={n} value={n}>Top {n}</option>)}
            <option value={0}>All ({ddrs.length})</option>
          </select>
        </div>

        {data.insurgent?.bottlers?.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={lbl}>Bottler</span>
            <select value={bottlerSel} onChange={e => setBottlerSel(e.target.value)} style={sel}>
              <option value="__all__">All</option>
              {data.insurgent.bottlers.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        )}

        <button onClick={() => setShowMap(m => !m)} style={{
          background: showMap ? T.accentSoft : "transparent",
          border: `1px solid ${showMap ? T.accent : T.border}`,
          color: showMap ? T.accent : T.textDim, padding: "5px 12px", borderRadius: 999,
          fontSize: 10.5, fontWeight: 700, cursor: "pointer" }}>
          🗺 Map {showMap ? "on" : "off"}
        </button>

        {ddrSel.length > 0 && (
          <button onClick={() => setDdrSel([])} style={{ color: T.accent, fontSize: 10.5,
            background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>
            ↻ Clear {ddrSel.length} DDR filter{ddrSel.length > 1 ? "s" : ""}
          </button>
        )}

        <span style={{ marginLeft: "auto", fontSize: 10, color: T.textDim, fontFamily: "monospace" }}>
          {byDdr.length} DDRs in scope · {sheetSel}
        </span>
      </div>

      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
        {/* ── KPIs ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
          <KPI label="CCI SSPET (<400)" value={fmtK(kpis.cciSspet)} delta={kpis.cciMom} icon="📦" color={T.accent} />
          <KPI label={`Aff CCI@${band}`} value={fmtK(kpis.affCci)} delta={kpis.affCciMom} icon="🥤" color={T.accentDark} />
          <KPI label={`PCI@${band} Outlets`} value={fmtK(kpis.pci)} delta={kpis.pciMom} icon="🏬" color="#1F3C88" />
          <KPI label={`Any Campa@${band}`} value={fmtK(kpis.campaAny)} delta={kpis.campa10Mom}
            subtitle={`${fmtPct(kpis.campaShare)} of stores`} icon="⚠️" color={T.warn} />
          <KPI label="Lahori Outlets" value={fmtK(kpis.lahori)} delta={kpis.lahoriMom} icon="🧊" color="#1A7A9A" />
          <KPI label="Rim-Zim Outlets" value={fmtK(kpis.rimzim)} delta={kpis.rimzimMom} icon="🍋" color="#E8701A" />
        </div>
          {/* ── State roll-up (mirrors the source deck's state view) ── */}
        <Card title={`${monthSel} : KO + Campa Outlets by State`}
          subtitle={`${byState.length} states · ${byDdr.length} DDRs rolled up · ${sheetSel}`}
          badge="BY STATE">
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={byState} margin={{ top: 20, right: 16, bottom: 30, left: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false} />
              <XAxis dataKey="code" tick={{ fill: T.text, fontSize: 11, fontWeight: 600 }}
                axisLine={{ stroke: T.border }} tickLine={false} interval={0} height={40} />
              <YAxis tickFormatter={fmtK} tick={{ fill: T.textDim, fontSize: 10 }}
                axisLine={{ stroke: T.border }} tickLine={false} />
              <Tooltip content={<Tip />} cursor={{ fill: `${T.accent}08` }} />
              <Legend {...legendProps} />
              {segments.map((seg, i) => (
                <Bar key={seg.key} dataKey={seg.key} name={seg.label} stackId="a"
                  fill={seg.color} stroke={seg.color === "#EDEDED" ? "#D0D0D0" : "none"}>
                  {/* Only label the dominant segment — labelling all five
                      reproduces the unreadable overlap in the source deck. */}
                  {seg.key === "exKo" && (
                    <LabelList dataKey={seg.key} position="insideTop" formatter={fmtK}
                      style={{ fill: "#fff", fontSize: 9.5, fontWeight: 700 }} />
                  )}
                  {i === segments.length - 1 && (
                    <LabelList dataKey="total" position="top" formatter={fmtK}
                      style={{ fill: T.textMid, fontSize: 9.5, fontWeight: 700 }} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>
   {/* ── Line charts ── */}
        <Card title="Number of outlets KO vs Campa"
          subtitle={lineSeries.map(l => l.label).join(" · ")} badge={`${months.length} periods`}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={byMonth} margin={{ top: 16, right: 20, bottom: 24, left: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fill: T.textDim, fontSize: 9.5 }} axisLine={{ stroke: T.border }}
                tickLine={false} angle={-40} textAnchor="end" height={48} interval={0} />
              <YAxis tickFormatter={fmtK} tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false} />
              <Tooltip content={<Tip />} />
              <Legend {...legendProps} />
              {lineSeries.map(l => (
                <Line key={l.key} type="monotone" dataKey={l.key} name={l.label} stroke={l.color}
                  strokeWidth={2} dot={{ r: 2.5, fill: l.color, strokeWidth: 0 }} activeDot={{ r: 5 }}>
                  <LabelList dataKey={l.key} position="top" formatter={fmtK}
                    style={{ fill: T.textDim, fontSize: 8.5 }} />
                </Line>
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Card>

        
        {/* ── Map + per-DDR stacked bars ── */}
        {showMap && mapData.length > 0 && (
          <Card title="Coverage vs Campa"
            subtitle={`KO coverage (1+2) ÷ Campa coverage (2+3+4) · ${monthSel} · ${sheetSel}`}
            badge="MAP">
            <MapErrorBoundary>
              <MapDDRWithKpiHeatmap kpiData={mapData} />
            </MapErrorBoundary>
          </Card>
        )}

     

      

        {/* ── Cross-worksheet comparison ── */}
        {/* {sheets.length > 1 && (
          <Card title="Store Band Comparison" badge="CROSS-SHEET"
            subtitle={`Total KO + Campa stores across ${sheets.join(" · ")}${ddrSel.length ? ` · ${ddrSel.length} DDRs` : " · all DDRs"}`}>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={crossSheet} margin={{ top: 16, right: 20, bottom: 28, left: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={T.border} />
                <XAxis dataKey="month" tick={{ fill: T.textDim, fontSize: 9.5 }} axisLine={{ stroke: T.border }}
                  tickLine={false} angle={-40} textAnchor="end" height={52} interval={0} />
                <YAxis tickFormatter={fmtK} tick={{ fill: T.textDim, fontSize: 10 }}
                  axisLine={{ stroke: T.border }} tickLine={false} />
                <Tooltip content={<Tip />} />
                <Legend {...legendProps} />
                {sheets.map((sh, i) => (
                  <Line key={sh} type="monotone" dataKey={sh} name={sh}
                    stroke={SHEET_COLORS[i % SHEET_COLORS.length]} strokeWidth={2.4}
                    dot={{ r: 3, fill: SHEET_COLORS[i % SHEET_COLORS.length], strokeWidth: 0 }}
                    activeDot={{ r: 5.5 }} />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        )} */}

        {/* ── Month trend ── */}
        {/* <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}> */}
          {/* <Card title="KO + Campa Stores" subtitle="Absolute outlet counts by month">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={byMonth} margin={{ top: 16, right: 12, bottom: 24, left: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false} />
                <XAxis dataKey="month" tick={{ fill: T.textDim, fontSize: 9.5 }} axisLine={{ stroke: T.border }}
                  tickLine={false} angle={-40} textAnchor="end" height={48} interval={0} />
                <YAxis tickFormatter={fmtK} tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false} />
                <Tooltip content={<Tip />} cursor={{ fill: `${T.accent}08` }} />
                <Legend {...legendProps} />
                {stackedBars()}
              </BarChart>
            </ResponsiveContainer>
          </Card> */}

          {/* "KO + Campa Stores : Outlet%" (100% stacked) removed on request.
              The absolute stacked chart above carries the same split. */}
        {/* </div> */}

     

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Card title="Lahori vs Rim-Zim reach" subtitle="Competitor Outlets">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={byMonth} margin={{ top: 16, right: 20, bottom: 24, left: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={T.border} />
                <XAxis dataKey="month" tick={{ fill: T.textDim, fontSize: 9.5 }} axisLine={{ stroke: T.border }}
                  tickLine={false} angle={-40} textAnchor="end" height={48} interval={0} />
                <YAxis tickFormatter={fmtK} tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false} />
                <Tooltip content={<Tip />} />
                <Legend {...legendProps} />
                {DDR_COMPETITORS.map(c => (
                  <Line key={c.key} type="monotone" dataKey={c.key} name={c.label} stroke={c.color}
                    strokeWidth={2.2} dot={{ r: 3, fill: c.color, strokeWidth: 0 }} activeDot={{ r: 5.5 }}>
                    <LabelList dataKey={c.key} position="top" formatter={fmtK}
                      style={{ fill: T.textDim, fontSize: 8.5 }} />
                  </Line>
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {data.insurgent ? (
            <Card title="Toxicity Metrics" badge="BUBBLE"
              subtitle={`${data.insurgent.headerLabels.wd} × ${data.insurgent.headerLabels.share} · bubble = ${data.insurgent.headerLabels.vol}`}>
              <ResponsiveContainer width="100%" height={360}>
                <ScatterChart margin={{ top: 16, right: 24, bottom: 34, left: 4 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke={T.border} />
                  <XAxis type="number" dataKey="x" name="Lead insurgent WD"
                    domain={[0, 1]} tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                    tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false}
                    label={{ value: "Lead insurgent weighted distribution →", position: "insideBottom",
                      offset: -8, fill: T.textDim, fontSize: 10 }} />
                  <YAxis type="number" dataKey="y" name="Insurgent share"
                    domain={[0, 1]} tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                    tick={{ fill: T.textDim, fontSize: 10 }} axisLine={{ stroke: T.border }} tickLine={false}
                    label={{ value: "Total insurgent share ↑", angle: -90, position: "insideLeft",
                      offset: 10, fill: T.textDim, fontSize: 10 }} />
                  <ZAxis type="number" dataKey="z" range={[60, 900]} name="Industry vol" />

                  {/* Median split — the top-right quadrant is where insurgents
                      have both reach AND share, i.e. an established position */}
                  <ReferenceLine x={data.insurgent.medWd} stroke={T.textDim} strokeDasharray="4 4" />
                  <ReferenceLine y={data.insurgent.medShare} stroke={T.textDim} strokeDasharray="4 4" />

                  <Tooltip cursor={{ strokeDasharray: "3 3", stroke: T.accent }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: "rgba(255,255,255,0.97)", border: `1px solid ${T.borderMed}`,
                          borderRadius: 8, padding: "10px 14px", fontSize: 11, boxShadow: T.shadowMd }}>
                          <div style={{ fontWeight: 700, marginBottom: 2 }}>{d.ddr}</div>
                          {d.bottler && <div style={{ fontSize: 9.5, color: T.textDim, marginBottom: 5 }}>{d.bottler}</div>}
                          <div style={{ color: T.textMid }}>Insurgent share: <b style={{ color: T.neg }}>{(d.y * 100).toFixed(1)}%</b></div>
                          <div style={{ color: T.textMid }}>Lead insurgent WD: <b style={{ color: T.text }}>{(d.x * 100).toFixed(0)}%</b></div>
                          <div style={{ color: T.textMid }}>Industry vol: <b style={{ color: T.text }}>{d.z.toFixed(2)}</b></div>
                        </div>
                      );
                    }} />

                  <Scatter data={insurgentPoints}>
                    {insurgentPoints.map((pt, i) => {
                      const wide = pt.x >= data.insurgent.medWd;
                      const strong = pt.y >= data.insurgent.medShare;
                      const fill = strong ? (wide ? T.neg : T.warn) : (wide ? T.accent : T.pos);
                      return <Cell key={i} fill={fill} fillOpacity={strong && wide ? 0.8 : 0.5} stroke={fill} />;
                    })}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8,
                fontSize: 9.5, color: T.textDim }}>
                {[["Entrenched (wide + high share)", T.neg], ["High share, low reach", T.warn],
                  ["Wide reach, low share", T.accent], ["Contained", T.pos]].map(([lab, c]) => (
                  <span key={lab} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: c, opacity: 0.7 }} />{lab}
                  </span>
                ))}
                <span style={{ marginLeft: "auto", fontStyle: "italic" }}>
                  dashed = medians · {insurgentPoints.length} DDRs · sheet "{data.insurgent.sheetName}"
                </span>
              </div>
            </Card>
          ) : (
            <Card title="Insurgent Brand Threat" badge="BUBBLE"
              subtitle="Add the insurgent-brands worksheet to populate this chart">
              <div style={{ padding: 60, textAlign: "center", color: T.textDim, fontSize: 12 }}>
                No insurgent sheet found. Expected columns:
                <div style={{ fontFamily: "monospace", fontSize: 10.5, marginTop: 8, color: T.textMid }}>
                  Bottler · DDR · SPK+ED Industry Vol UC · Total Insurgent Share (%) · Lead Insurgent WD (%)
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* ── Movers ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
          <Card title={`Biggest Movers — Aff KO@${band} Distribution`} badge="MoM"
            subtitle={`Outlet gain / loss vs prior month · ${monthSel} · ${sheetSel}`}>
            {topMovers.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 12 }}>
                No prior month to compare against.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(260, topMovers.length * 24 + 40)}>
                <BarChart data={topMovers} layout="vertical" margin={{ top: 8, right: 60, bottom: 8, left: 120 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke={T.border} horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtK} tick={{ fill: T.textDim, fontSize: 10 }}
                    axisLine={{ stroke: T.border }} tickLine={false} />
                  <YAxis type="category" dataKey="ddr" tick={{ fill: T.textMid, fontSize: 10 }}
                    axisLine={false} tickLine={false} width={120} interval={0} />
                  <Tooltip content={<Tip />} cursor={{ fill: `${T.accent}08` }} />
                  <Bar dataKey="delta" name={`Net Aff KO@${band} change`} radius={[3, 3, 3, 3]}>
                    {topMovers.map((m, i) => <Cell key={i} fill={m.delta >= 0 ? T.pos : T.neg} fillOpacity={0.85} />)}
                    <LabelList dataKey="delta" position="right" formatter={fmtK}
                      style={{ fill: T.textMid, fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

        </div>

          <Card title={`${monthSel} : KO + Campa Outlets by DDR`}
          subtitle={`${monthSel} · Top ${topN > 0 ? Math.min(topN, byDdr.length) : byDdr.length} of ${byDdr.length} DDRs by total stores · ${sheetSel}`}
          badge="BY DDR">
          <ResponsiveContainer width="100%" height={440}>
            <BarChart data={byDdrTop} layout="horizontal" margin={{ top: 24, right: 16, bottom: 90, left: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false} />
              <XAxis type="category" dataKey="ddr" tick={{ fill: T.textMid, fontSize: 9.5 }}
                axisLine={{ stroke: T.border }} tickLine={false}
                angle={-45} textAnchor="end" interval={0} height={90} />
              <YAxis type="number" tickFormatter={fmtK} tick={{ fill: T.textDim, fontSize: 10 }}
                axisLine={{ stroke: T.border }} tickLine={false} />
              <Tooltip content={<Tip />} cursor={{ fill: `${T.accent}08` }} />
              <Legend {...legendProps} />
              {segments.map((seg, i) => (
                <Bar key={seg.key} dataKey={seg.key} name={seg.label} stackId="a"
                  fill={seg.color} stroke={seg.color === "#EDEDED" ? "#D0D0D0" : "none"}>
                  {i === segments.length - 1 && (
                    <LabelList dataKey="total" position="top" formatter={fmtK}
                      style={{ fill: T.textMid, fontSize: 9.5, fontWeight: 700 }} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── WD / VPO / IPS — always last, under the movers ── */}
        {data.wd && (
          <WdTableView data={data.wd} onAskAI={onAskAI}
            bandLock={wdBandLock} embedded />
        )}
      </div>
      </>)}
    </div>
  );
}

const lbl = {
  color: T.textDim, fontSize: 9.5, letterSpacing: 1.2,
  textTransform: "uppercase", fontWeight: 700,
};
const sel = {
  background: T.bgAlt, color: T.text, border: `1px solid ${T.border}`,
  borderRadius: 6, padding: "4px 8px", fontSize: 11, cursor: "pointer",
};

const th = (align = "right") => ({
  padding: "6px 8px", color: T.textDim, fontSize: 9, textTransform: "uppercase",
  letterSpacing: 1, borderBottom: `1px solid ${T.border}`, textAlign: align, whiteSpace: "nowrap",
});
const td = (align = "right") => ({
  padding: "5px 8px", borderBottom: `1px solid ${T.border}`, textAlign: align, color: T.text,
});


/* A broken map must not take the whole dashboard down with it. */
class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 24, textAlign: "center", color: T.textDim, fontSize: 12 }}>
          Map failed to render — the rest of the dashboard is unaffected.
          <div style={{ marginTop: 6, fontSize: 11, color: T.neg }}>{String(this.state.err.message)}</div>
        </div>
      );
    }
    return this.props.children;
  }
} 